import { useActiveAddress } from "@arweave-wallet-kit/react"
import { createDataItemSigner } from "@permaweb/aoconnect"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { enqueueSnackbar } from "notistack"
import { useEffect } from "react"

import { AoInstance, Message, useAo } from "./ao"
import { AGENTS_FACTORY, REFRESH_INTERVAL_QUICK } from "../settings"
import { getTagValue } from "../utils/arweave"

export async function getSimpleAgent(user: string, ao: AoInstance) {
  const res = await ao.dryrun({
    process: AGENTS_FACTORY,
    tags: [
      { name: "Action", value: "Get-Simple-Agent-For-User" },
      { name: "User", value: user },
      { name: "Simple-Agent-Type", value: "hopper" },
    ],
  })

  for (const msg of res.Messages as Message[]) {
    const multiHopSwapAgent = getTagValue("Multi-Hop-Swap-Agent", msg.Tags)

    if (!multiHopSwapAgent) continue

    return multiHopSwapAgent === "" ? undefined : multiHopSwapAgent
  }

  return undefined
}

export type HopperAgentInfo = Record<string, string>

export async function getSimpleAgentInfo(
  agent: string,
  ao: AoInstance,
): Promise<HopperAgentInfo | undefined> {
  const res = await ao.dryrun({
    process: agent,
    tags: [{ name: "Action", value: "Info" }],
  })

  for (const msg of res.Messages as Message[]) {
    const tags = msg.Tags

    if (!tags) continue

    const tagsObject = tags.reduce(
      (acc, tag) => {
        acc[tag.name] = tag.value
        return acc
      },
      {} as { [key: string]: string },
    )

    // Patch IsStuck because the agent does not update until it receives a new message (it's not a cron)
    if (tagsObject["SwapStartTimestamp"]) {
      const timestamp = Number(tagsObject["SwapStartTimestamp"])
      const isStuck = !isNaN(timestamp) && new Date().getTime() - timestamp > 10 * 60 * 1000 // 10min

      tagsObject["IsStuck"] = String(isStuck)
    }

    return tagsObject
  }

  return undefined
}

export function useGetSimpleAgent(user?: string) {
  // ao instance
  const ao = useAo()

  const { data, isLoading, refetch, isRefetching } = useQuery({
    queryKey: ["Get-Simple-Agent", user, ao],
    queryFn: async () => {
      return await getSimpleAgent(user as string, ao)
    },
    retry: false,
    enabled: !!user,
  })

  useEffect(() => {
    if (!data) return

    const interval = setInterval(() => {
      refetch()
    }, REFRESH_INTERVAL_QUICK)

    return () => clearInterval(interval)
  }, [data, refetch])

  return { data, isLoading, refetch, isRefetching }
}

export async function deploySimpleAgent(ao: AoInstance) {
  const res = await ao.message({
    process: AGENTS_FACTORY,
    tags: [
      {
        name: "Action",
        value: "Create-Simple-Agent",
      },
      {
        name: "Simple-Agent-Type",
        value: "hopper",
      },
    ],
    signer: createDataItemSigner(window.arweaveWallet),
  })

  return res
}

export function useDeployHopperAgent() {
  const ao = useAo()
  const activeAddress = useActiveAddress()
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ["deployHopperAgent"],
    mutationFn: async () => {
      const result = await deploySimpleAgent(ao)

      enqueueSnackbar("Waiting for agent spawn...", {
        variant: "info",
        key: result,
      })

      // run getSimpleAgent until the agent is spawned and only then mark the mutation as successful
      let agent: string | undefined
      let tries = 0

      while (!agent) {
        if (tries > 5) {
          throw new Error("Agent spawn timeout")
        }

        agent = await getSimpleAgent(activeAddress as string, ao)

        console.log("AGENT_SPAWN", agent)

        if (agent === undefined) {
          const delay = 3000 * 2 ** tries
          await new Promise((resolve) => setTimeout(resolve, delay))
        }

        queryClient.invalidateQueries({
          queryKey: ["Get-Simple-Agent", activeAddress],
        })

        tries++
      }

      enqueueSnackbar("Hopper agent spawned successfully.", {
        variant: "success",
        key: result,
      })

      return result
    },
  })
}
